import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore, type Hat } from '../store/authStore';

/**
 * The two-hat switch for an operator with a home lender.
 *
 * Rendered in place of the static role pill (desktop header) and in the
 * phone menu's account block. Two segments — the operator label (Admin
 * or Operatør, from the ACCOUNT, so it reads right while the lender hat
 * is worn) and Långiver — inside one hairline border; the worn hat is
 * the filled segment. Same 10px mono uppercase register as the pill it
 * replaces, no radius, no shadow.
 *
 * Switching mints a token of the other kind (`POST /auth/hat`), then
 * empties the query cache — every list the operator had open was
 * fetched with the other hat's scope — and lands on /entries, which
 * both hats can see. Both segments are disabled while a switch is in
 * flight; a failure just re-enables them (the store is untouched until
 * the response arrives).
 */
export function HatToggle({ tall = false }: { tall?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canSwitchHat = useAuthStore((s) => s.canSwitchHat);
  const switchHat = useAuthStore((s) => s.switchHat);
  const [busy, setBusy] = useState(false);

  if (!user || !canSwitchHat()) return null;

  const worn: Hat = user.hat ?? 'operator';
  const operatorLabel = user.account_is_admin
    ? t('auth.roleAdmin')
    : t('auth.roleOperator');

  const pick = async (hat: Hat) => {
    if (busy || hat === worn) return;
    setBusy(true);
    try {
      await switchHat(hat);
      queryClient.clear();
      navigate('/entries', { replace: true });
    } catch (err) {
      console.error('hat switch failed', err);
    } finally {
      setBusy(false);
    }
  };

  const segment = (hat: Hat, label: string) => {
    const active = hat === worn;
    return (
      <button
        key={hat}
        type="button"
        onClick={() => pick(hat)}
        aria-pressed={active}
        disabled={busy}
        className={`font-mono text-[10px] uppercase tracking-[0.14em] px-2 transition-colors disabled:cursor-wait ${
          tall ? 'min-h-11 py-2' : 'py-0.5'
        } ${
          active
            ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary-strong)]'
            : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      role="group"
      aria-label={t('header.switchHat')}
      title={t('header.switchHat')}
      className="inline-flex items-stretch border border-[var(--color-rule)] divide-x divide-[var(--color-rule)]"
    >
      {segment('operator', operatorLabel)}
      {segment('lender', t('auth.roleLenderHuman'))}
    </div>
  );
}
